import { useCallback, useState } from "react";
import { Search } from "lucide-react";
import Button from "../components/Button";
import DateRangeFilter from "../components/DateRangeFilter";
import FilterDropdown from "../components/FilterDropdown";
import Pagination from "../components/Pagination";
import SectionHeader from "../components/SectionHeader";
import StatCard, { StatGrid } from "../components/StatCard";
import Tag from "../components/Tag";
import controlStyles from "../components/ListControls.module.css";
import tableStyles from "../components/DataTable.module.css";
import TextLink from "../components/TextLink";
import downloadCsv from "../downloadCsv";
import { apiRequest } from "../api";
import { formatDateTime, formatNumber, formatSignedPercent, formatUsd } from "../format";
import useApiQuery, { useDebounced } from "../useApiQuery";
import AccountDrawer from "./AccountDrawer";
import {
  ACCOUNT_STAT_LABELS,
  ACCOUNT_STATUS,
  ACCOUNT_TYPES,
  KYC_STATUS,
  WITHDRAWAL_STATUS,
} from "./accountsData";
import styles from "./AccountsPage.module.css";

const PAGE_SIZE = 15;

// Widths include the cell gutters and add up to the table's min-width
const COLUMNS = [
  { label: "Account ID", width: 126 },
  { label: "Trader", width: 120 },
  { label: "Type", width: 120 },
  { label: "Status", width: 220 },
  { label: "Balance", width: 120 },
  { label: "Equity", width: 120 },
  { label: "Daily DD", width: 120 },
  { label: "Max DD", width: 120 },
  { label: "KYC", width: 140 },
  { label: "Withdrawal status", width: 180 },
  { label: "Last active", width: 186 },
];

const toOptions = (map) =>
  Object.entries(map).map(([value, entry]) =>
    typeof entry === "string" ? { value, label: entry } : { value, label: entry.label, description: entry.description }
  );

const FILTERS = [
  { key: "type", label: "Account Type", options: toOptions(ACCOUNT_TYPES) },
  { key: "status", label: "Status", options: toOptions(ACCOUNT_STATUS) },
  { key: "kyc", label: "KYC", options: toOptions(KYC_STATUS) },
  { key: "withdrawal", label: "Withdrawal status", options: toOptions(WITHDRAWAL_STATUS) },
];

const EMPTY_FILTERS = {
  type: null,
  status: null,
  kyc: null,
  withdrawal: null,
  created: { from: "", to: "" },
};

// Most the CSV export pulls in one go
const EXPORT_LIMIT = 10000;

// The API's filter params for the current toolbar state
function toQuery(filters, query) {
  return {
    q: query.trim(),
    type: filters.type,
    status: filters.status,
    kyc: filters.kyc,
    withdrawal: filters.withdrawal,
    createdFrom: filters.created.from,
    createdTo: filters.created.to,
  };
}

// Red once the rule is breached, amber past half the limit
function drawdownTone(drawdown) {
  if (!drawdown) {
    return null;
  }
  if (drawdown.breached || drawdown.pct >= drawdown.limitPct) {
    return "danger";
  }
  return drawdown.pct >= drawdown.limitPct / 2 ? "warning" : "success";
}

function toneClass(tone) {
  return tone ? styles[`tone${tone[0].toUpperCase()}${tone.slice(1)}`] : "";
}

const formatDrawdown = (drawdown) => (drawdown ? formatSignedPercent(-drawdown.pct) : "—");

function cells(account) {
  return {
    balance: formatUsd(account.balance, 2),
    equity: formatUsd(account.equity, 2),
    dailyDd: formatDrawdown(account.dailyDd),
    maxDd: formatDrawdown(account.maxDd),
    withdrawal: WITHDRAWAL_STATUS[account.withdrawal]?.label ?? formatSignedPercent(account.progressPct),
    lastActive: formatDateTime(account.lastActive),
  };
}

async function exportCsv(query) {
  const { accounts } = await apiRequest("/api/admin/accounts", {
    query: { ...query, page: 1, pageSize: EXPORT_LIMIT },
  });
  const rows = accounts.map((account) => {
    const cell = cells(account);
    return [
      account.login,
      account.trader,
      ACCOUNT_TYPES[account.type],
      ACCOUNT_STATUS[account.status].label,
      cell.balance,
      cell.equity,
      cell.dailyDd,
      cell.maxDd,
      KYC_STATUS[account.kyc].label,
      cell.withdrawal,
      cell.lastActive,
    ];
  });
  downloadCsv(
    "accounts.csv",
    COLUMNS.map((column) => column.label),
    rows
  );
}

export default function AccountsPage() {
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Search (MT5 login, trader name, email) and filters run on the API
  const debouncedQuery = useDebounced(query);
  const apiQuery = toQuery(filters, debouncedQuery);
  const { data, status: loadStatus, reload } = useApiQuery("/api/admin/accounts", {
    ...apiQuery,
    page,
    pageSize: PAGE_SIZE,
  });

  const visible = loadStatus === "error" ? [] : data?.accounts ?? [];
  const pageCount = Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportCsv(apiQuery);
    } catch (err) {
      console.error("Account export failed:", err);
    } finally {
      setExporting(false);
    }
  };

  const updateFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
    setPage(1);
  };

  const closeDrawer = useCallback(() => setSelected(null), []);

  return (
    <section className={styles.page}>
      <h1 className={styles.title}>Accounts</h1>

      <section>
        <SectionHeader title="Overview" />
        <StatGrid>
          {ACCOUNT_STAT_LABELS.map(({ key, label }) => (
            <StatCard key={key} label={label} value={formatNumber(data?.stats[key])} />
          ))}
        </StatGrid>
      </section>

      <section>
        <div className={`${controlStyles.toolbar} d-flex align-items-center justify-content-between`}>
          <label className={`${controlStyles.search} d-flex align-items-center`}>
            <Search size={20} strokeWidth={1.75} aria-hidden="true" />
            <input
              type="search"
              placeholder="Search traders, emails or logins"
              aria-label="Search traders"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setPage(1);
              }}
            />
          </label>
          <Button className={styles.export} onClick={handleExport} disabled={exporting || !data?.total}>
            {exporting ? "Exporting…" : "Export"}
          </Button>
        </div>

        <div className={`${controlStyles.filters} d-flex flex-wrap`}>
          {FILTERS.map((filter) => (
            <FilterDropdown
              key={filter.key}
              label={filter.label}
              options={filter.options}
              value={filters[filter.key]}
              onChange={(value) => updateFilter(filter.key, value)}
            />
          ))}
          <DateRangeFilter
            label="Created date"
            value={filters.created}
            onChange={(value) => updateFilter("created", value)}
          />
        </div>

        <div className={tableStyles.wrap}>
          <table className={`${tableStyles.table} ${tableStyles.fixed} ${styles.table}`}>
            <colgroup>
              {COLUMNS.map((column) => (
                <col key={column.label} style={{ width: column.width }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                {COLUMNS.map((column) => (
                  <th key={column.label} scope="col">
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((account) => {
                const status = ACCOUNT_STATUS[account.status];
                const kyc = KYC_STATUS[account.kyc];
                const withdrawal = WITHDRAWAL_STATUS[account.withdrawal];
                const cell = cells(account);

                return (
                  <tr
                    key={account.id}
                    className={tableStyles.clickable}
                    onClick={() => setSelected(account)}
                    onKeyDown={(event) => event.key === "Enter" && setSelected(account)}
                    tabIndex={0}
                  >
                    <td>{account.login ?? "—"}</td>
                    <td>{account.trader}</td>
                    <td>{ACCOUNT_TYPES[account.type]}</td>
                    <td title={status.description}>
                      <Tag tone={status.tone}>{status.label}</Tag>
                    </td>
                    <td>{cell.balance}</td>
                    <td>{cell.equity}</td>
                    <td className={toneClass(drawdownTone(account.dailyDd))}>{cell.dailyDd}</td>
                    <td className={toneClass(drawdownTone(account.maxDd))}>{cell.maxDd}</td>
                    <td>
                      <Tag tone={kyc.tone}>{kyc.label}</Tag>
                    </td>
                    <td>
                      {withdrawal ? (
                        <Tag tone={withdrawal.tone}>{withdrawal.label}</Tag>
                      ) : (
                        cell.withdrawal
                      )}
                    </td>
                    <td>{cell.lastActive}</td>
                  </tr>
                );
              })}
              {!data && loadStatus === "loading" ? (
                <tr>
                  <td colSpan={COLUMNS.length} className={controlStyles.empty}>
                    Loading accounts…
                  </td>
                </tr>
              ) : loadStatus === "error" ? (
                <tr>
                  <td colSpan={COLUMNS.length} className={controlStyles.empty}>
                    We couldn't load accounts.{" "}
                    <TextLink arrow={false} onClick={reload}>
                      Try again
                    </TextLink>
                  </td>
                </tr>
              ) : visible.length === 0 ? (
                <tr>
                  <td colSpan={COLUMNS.length} className={controlStyles.empty}>
                    No accounts match these filters.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <Pagination page={currentPage} pageCount={pageCount} onChange={setPage} />
      </section>

      <AccountDrawer account={selected} onClose={closeDrawer} onChanged={reload} />
    </section>
  );
}
